<template>
  <Loader :value="promises.fetch" class="form-view" :style="style">
    <div class="container view">
      <HeaderComponent :title="header.title" :breadcrumb="header.breadcrumb">
        <template #actions>
          <ButtonComponent v-for="item in actions.secondary" v-bind="item" />
          <ButtonComponent
            v-if="actions.primary"
            theme="primary"
            :promise="promises.submit"
            :disabled="!validator.passes"
            v-bind="actions.primary"
            @click="submit"
          />
        </template>
      </HeaderComponent>

      <Card tag="form" @submit.prevent="submit">
        <CustomFields v-model="data" :schema="schema" :validator="validator" @action="action" />
      </Card>

      <div class="form-view__footer-actions">
        <ButtonComponent v-for="item in actions.footer" v-bind="item" />
      </div>
    </div>
  </Loader>
</template>

<script>
import Validator from '@/mixins/validator-v2'

import fetch from '@/fetch'
import Card from '@/components/Card'
import CustomFields, { getValidatorConfig } from '@kvass/custom-fields'
import HeaderComponent from '@/components/Header'
import { ButtonComponent } from 'vue-elder-button'
import { LoaderComponent as Loader } from 'vue-elder-loader'

const defaultData = {
  header: { title: '', breadcrumb: [] },
  actions: { primary: null, secondary: [], footer: [] },
  schema: [],
  style: {},
}

export default {
  mixins: [
    Validator({
      rules: 'validatorConfig.rules',
      data: 'data',
      labels: 'validatorConfig.labels',
    }),
  ],
  props: {
    target: String,
  },
  watch: {
    target: {
      handler: 'fetch',
      immediate: true,
      deep: true,
    },
  },
  data() {
    return {
      ...structuredClone(defaultData),
      data: {},
      promises: {
        submit: null,
        fetch: null,
      },
    }
  },
  computed: {
    validatorConfig() {
      return getValidatorConfig(this.schema, this.data)
    },
  },
  methods: {
    reset() {
      Object.entries(defaultData).forEach(([k, v]) => (this[k] = structuredClone(v)))
    },
    submit() {
      this.promises.submit = fetch(this.target, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.data),
      })
        .then(res => res.json())
        .then(data => this.action(data))
    },
    action(data = {}) {
      if (data instanceof CustomEvent) data = data.detail
      const { type, value } = data
      switch (type) {
        case 'navigate':
          return this.$router.push(value).then(() => this.reset() /* remove loading state on primary button */)
        case 'redirect':
          return (window.location.href = value)
        case 'update':
          return this.setData(value)
        case 'refetch':
          return this.fetch()
      }
    },
    setData(data) {
      this.reset()
      this.data = 'data' in data ? data.data : {}
      ;['header', 'actions', 'schema', 'style'].forEach(key => {
        if (!data[key]) return
        this[key] = data[key]
      })
    },
    fetch() {
      if (!this.target) return

      this.promise = fetch(this.target, { method: 'GET' })
        .then(res => res.json())
        .then(data => this.setData(data))
    },
  },
  metaInfo() {
    return {
      title: this.header.title || this.$t('loading'),
    }
  },
  created() {
    this.reset()
  },
  components: {
    Card,
    CustomFields,
    ButtonComponent,
    HeaderComponent,
    Loader,
  },
}
</script>

<style lang="scss">
.form-view {
  background-color: $background-grey;

  & > .view > .card {
    background-color: var(--form-card-background, white);
  }

  & > .view > .card > .card__content {
    --card-spacing: var(--form-card-spacing, var(--card-spacing));
  }

  &__footer-actions {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-top: 2rem;
  }
}
</style>
